/**
 * API Service for Cortex Chat
 * Handles all API calls to the FastAPI backend with TypeScript types
 *
 * Optimizations:
 * - Conversation caching to avoid re-fetching on session switch
 * - Request deduplication to prevent duplicate API calls
 * - AbortController support for cancelling pending requests
 */

import type {
  AskQuestionRequest,
  AskQuestionResponse,
  LoadConversationResponse,
  GetSessionsResponse,
  ClearConversationRequest,
  ClearConversationResponse,
  UpdateSessionTitleRequest,
  UpdateSessionTitleResponse,
  GenerateSmartQuestionsRequest,
  GenerateSmartQuestionsResponse,
  TimingBreakdown,
} from './types';

const API_BASE_URL: string = process.env.REACT_APP_API_URL || 'http://localhost:8002';

// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

// Conversation cache interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  messageCount?: number; // For incremental loading
}

class CortexChatAPI {
  private baseUrl: string;

  // ===========================================
  // OPTIMIZATION: Conversation caching
  // ===========================================
  private conversationCache: Map<string, CacheEntry<LoadConversationResponse>> = new Map();
  private sessionsCache: CacheEntry<GetSessionsResponse> | null = null;

  /**
   * Log timing breakdown to browser console in a formatted way
   */
  private logTimingBreakdown(timing: TimingBreakdown, question: string): void {
    const cortexTotal = timing.total_ms || 0;
    const apiTotal = timing.total_api_time_ms || 0;

    console.group('%c[CORTEX DEBUG] API Response Timing', 'color: #4CAF50; font-weight: bold;');
    console.log('%cQuestion: %s', 'color: #2196F3;', question);

    // Cortex Processing Breakdown
    console.group(`%c[CORTEX PROCESSING BREAKDOWN] (${cortexTotal.toFixed(2)}ms total):`, 'color: #FF9800; font-weight: bold;');
    console.log(`  ├─ Intent Classification: ${(timing.intent_classification_ms || 0).toFixed(2)}ms`);
    console.log(`  ├─ Authentication: ${(timing.authentication_ms || 0).toFixed(2)}ms`);
    console.log(`  ├─ Context Building: ${(timing.context_building_ms || 0).toFixed(2)}ms`);
    console.log(`  ├─ Cortex Analyst Query (SQL Generation): ${(timing.cortex_analyst_query_ms || 0).toFixed(2)}ms`);
    console.log(`  ├─ SQL Execution: ${(timing.sql_execution_ms || 0).toFixed(2)}ms`);
    console.log(`  ├─ Response Generation (LLM): ${(timing.response_generation_ms || 0).toFixed(2)}ms`);
    console.log(`  ├─ Data Summary Generation: ${(timing.data_summary_ms || 0).toFixed(2)}ms`);
    console.log(`  └─ LaTeX Conversion: ${(timing.latex_conversion_ms || 0).toFixed(2)}ms`);
    console.groupEnd();

    // API Level Breakdown
    console.group('%c[API LEVEL BREAKDOWN]:', 'color: #9C27B0; font-weight: bold;');
    console.log(`  ├─ Cortex Processing (Total): ${cortexTotal.toFixed(2)}ms`);
    console.log(`  ├─ Visualization + Suggestions: ${(timing.visualization_suggestions_ms || 0).toFixed(2)}ms`);
    console.log(`  └─ Database Save: ${(timing.database_save_ms || 0).toFixed(2)}ms`);
    console.groupEnd();

    console.log(`%c[TOTAL API TIME]: ${apiTotal.toFixed(2)}ms`, 'color: #f44336; font-weight: bold; font-size: 14px;');
    console.groupEnd();

    // Also log as a table for easy viewing
    console.table({
      'Intent Classification': `${(timing.intent_classification_ms || 0).toFixed(2)}ms`,
      'Authentication': `${(timing.authentication_ms || 0).toFixed(2)}ms`,
      'Context Building': `${(timing.context_building_ms || 0).toFixed(2)}ms`,
      'Cortex Analyst Query': `${(timing.cortex_analyst_query_ms || 0).toFixed(2)}ms`,
      'SQL Execution': `${(timing.sql_execution_ms || 0).toFixed(2)}ms`,
      'Response Generation': `${(timing.response_generation_ms || 0).toFixed(2)}ms`,
      'Data Summary': `${(timing.data_summary_ms || 0).toFixed(2)}ms`,
      'LaTeX Conversion': `${(timing.latex_conversion_ms || 0).toFixed(2)}ms`,
      'Visualization + Suggestions': `${(timing.visualization_suggestions_ms || 0).toFixed(2)}ms`,
      'Database Save': `${(timing.database_save_ms || 0).toFixed(2)}ms`,
      'TOTAL': `${apiTotal.toFixed(2)}ms`,
    });
  }

  // ===========================================
  // OPTIMIZATION: Request deduplication
  // ===========================================
  private pendingRequests: Map<string, Promise<any>> = new Map();

  // ===========================================
  // OPTIMIZATION: AbortController for cancellation
  // ===========================================
  private abortControllers: Map<string, AbortController> = new Map();

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid<T>(entry: CacheEntry<T> | null | undefined): boolean {
    if (!entry) return false;
    return Date.now() - entry.timestamp < CACHE_TTL;
  }

  /**
   * Get or create AbortController for a request
   */
  private getAbortController(key: string): AbortController {
    // Cancel any existing request with the same key
    const existing = this.abortControllers.get(key);
    if (existing) {
      existing.abort();
    }

    const controller = new AbortController();
    this.abortControllers.set(key, controller);
    return controller;
  }

  /**
   * Clean up AbortController after request completes
   */
  private cleanupAbortController(key: string): void {
    this.abortControllers.delete(key);
  }

  /**
   * Deduplicate requests - return existing promise if same request is in flight
   */
  private async deduplicateRequest<T>(
    key: string,
    requestFn: () => Promise<T>
  ): Promise<T> {
    // Check if same request is already in flight
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending as Promise<T>;
    }

    // Create new request and track it
    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  /**
   * Ask a question
   */
  async askQuestion(
    question: string,
    sessionId: string | null = null,
    userId: number | null = null,
    userType: string = 'guest'
  ): Promise<AskQuestionResponse> {
    const requestBody: any = {
      question,
      session_id: sessionId,
      user_type: userType,
    };

    // Only include user_id if user is logged in
    if (userId !== null) {
      requestBody.user_id = userId;
    }

    const response = await fetch(`${this.baseUrl}/ask_question`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody as AskQuestionRequest),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }

    const result: AskQuestionResponse = await response.json();

    // Log timing breakdown to browser console for debugging
    if (result.timing_breakdown) {
      this.logTimingBreakdown(result.timing_breakdown, question);
    }

    // Invalidate conversation cache for this session since we added new messages
    if (result.session_id) {
      this.conversationCache.delete(result.session_id);
    }

    // Invalidate sessions cache since counts may have changed
    this.sessionsCache = null;

    return result;
  }

  /**
   * Load conversation history with caching
   */
  async loadConversation(
    sessionId: string,
    userId: number | null = null,
    userType: string = 'guest',
    forceRefresh: boolean = false
  ): Promise<LoadConversationResponse> {
    const cacheKey = `${sessionId}-${userId}-${userType}`;

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = this.conversationCache.get(sessionId);
      if (this.isCacheValid(cached)) {
        return cached!.data;
      }
    }

    // Deduplicate concurrent requests for same conversation
    return this.deduplicateRequest(
      `loadConversation-${cacheKey}`,
      async () => {
        const controller = this.getAbortController(`loadConversation-${sessionId}`);

        try {
          let url = `${this.baseUrl}/load_conversation?session_id=${encodeURIComponent(sessionId)}&user_type=${encodeURIComponent(userType)}`;

          // Only include user_id if user is logged in
          if (userId !== null) {
            url += `&user_id=${userId}`;
          }

          const response = await fetch(url, {
            signal: controller.signal,
          });

          if (!response.ok) {
            throw new Error(`API error: ${response.statusText}`);
          }

          const result = await response.json();

          // Cache the result
          this.conversationCache.set(sessionId, {
            data: result,
            timestamp: Date.now(),
            messageCount: result.count,
          });

          return result;
        } catch (error) {
          // Don't throw for aborted requests (user navigated away)
          if (error instanceof Error && error.name === 'AbortError') {
            return { success: false, history: [], count: 0 } as LoadConversationResponse;
          }
          throw error;
        } finally {
          this.cleanupAbortController(`loadConversation-${sessionId}`);
        }
      }
    );
  }

  /**
   * Get all user sessions with caching
   */
  async getSessions(
    userId: number | null = null,
    userType: string = 'guest',
    forceRefresh: boolean = false
  ): Promise<GetSessionsResponse> {
    // Check cache first (unless force refresh)
    if (!forceRefresh && this.isCacheValid(this.sessionsCache)) {
      return this.sessionsCache!.data;
    }

    const cacheKey = `getSessions-${userId}-${userType}`;

    // Deduplicate concurrent requests
    return this.deduplicateRequest(cacheKey, async () => {
      const controller = this.getAbortController('getSessions');

      try {
        let url = `${this.baseUrl}/get_sessions?user_type=${encodeURIComponent(userType)}`;

        // Only include user_id if user is logged in
        if (userId !== null) {
          url += `&user_id=${userId}`;
        }

        const response = await fetch(url, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.statusText}`);
        }

        const result = await response.json();

        // Cache the result
        this.sessionsCache = {
          data: result,
          timestamp: Date.now(),
        };

        return result;
      } catch (error) {
        // Don't throw for aborted requests (user navigated away)
        if (error instanceof Error && error.name === 'AbortError') {
          return { success: false, count: 0, sessions: [] } as GetSessionsResponse;
        }
        throw error;
      } finally {
        this.cleanupAbortController('getSessions');
      }
    });
  }

  /**
   * Clear/delete a conversation
   */
  async clearConversation(
    sessionId: string,
    userId: number | null = null,
    userType: string = 'guest'
  ): Promise<ClearConversationResponse> {
    const requestBody: any = {
      session_id: sessionId,
      user_type: userType,
    };

    // Only include user_id if user is logged in
    if (userId !== null) {
      requestBody.user_id = userId;
    }

    const response = await fetch(`${this.baseUrl}/clear_conversation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody as ClearConversationRequest),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }

    const result = await response.json();

    // Invalidate caches
    this.conversationCache.delete(sessionId);
    this.sessionsCache = null;

    return result;
  }

  /**
   * Update session title
   */
  async updateSessionTitle(
    sessionId: string,
    title: string,
    userId: number | null = null,
    userType: string = 'guest'
  ): Promise<UpdateSessionTitleResponse> {
    const requestBody: any = {
      session_id: sessionId,
      title,
      user_type: userType,
    };

    // Only include user_id if user is logged in
    if (userId !== null) {
      requestBody.user_id = userId;
    }

    const response = await fetch(`${this.baseUrl}/update_session_title`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody as UpdateSessionTitleRequest),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }

    const result = await response.json();

    // Invalidate sessions cache since title changed
    this.sessionsCache = null;

    return result;
  }

  /**
   * Generate smart questions for a topic
   */
  async generateSmartQuestions(
    topic: string,
    sessionId: string | null = null
  ): Promise<GenerateSmartQuestionsResponse> {
    const cacheKey = `smartQuestions-${topic}`;

    // Deduplicate concurrent requests for same topic
    return this.deduplicateRequest(cacheKey, async () => {
      const response = await fetch(`${this.baseUrl}/generate_smart_questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          topic,
          session_id: sessionId,
        } as GenerateSmartQuestionsRequest),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      return await response.json();
    });
  }

  /**
   * Cancel all pending requests (useful on unmount)
   */
  cancelAllRequests(): void {
    this.abortControllers.forEach((controller) => {
      controller.abort();
    });
    this.abortControllers.clear();
    this.pendingRequests.clear();
  }

  /**
   * Clear all caches (useful on logout)
   */
  clearCache(): void {
    this.conversationCache.clear();
    this.sessionsCache = null;
  }

  /**
   * Invalidate session cache (useful after sending a message)
   */
  invalidateSessionCache(sessionId: string): void {
    this.conversationCache.delete(sessionId);
    this.sessionsCache = null;
  }
}

export const api = new CortexChatAPI();
export default api;
